import {
  Button,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { LoadingButton } from "src/grading/components";
import { inputError } from "src/grading/components/InputError/InputError";
import RFModal from "src/grading/components/RFModal/RFModal";
import { DEFAULT_ORGANIZATION } from "src/grading/models";
import { useAppSelector } from "src/store/hooks";
import { selectCurrentOrgID, selectCurrentUser } from "src/store/reducers/auth/authSlice";
import { selectCurrentRoles } from "src/store/reducers/organizations/organizationSlice";
import { useInviteMemberMutation } from "src/store/reducers/organizations/organizationsApiSlice";
import theme from "src/theme";
import * as Yup from "yup";
import useStyles from "./InviteMemberModal.styles";

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email").required("Email is required"),
  role: Yup.string().required("Role is required"),
});

interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
}

export interface InviteMemberFormValues {
  email: string;
  role: string;
}

const InviteMemberModal = (props: InviteMemberModalProps) => {
  const classes = useStyles();

  const roles = useAppSelector(selectCurrentRoles);

  const currentOrgID = useAppSelector(selectCurrentOrgID);

  const [inviteMember, { isLoading }] = useInviteMemberMutation();

  const saveMember = async (values: InviteMemberFormValues) => {
    const result = await inviteMember({
      organizationId: currentOrgID ?? DEFAULT_ORGANIZATION.id,
      payload: values,
    }).unwrap();
    if (result.success) {
      props.onClose();
    }
  };

  const formik = useFormik({
    initialValues: {
      email: "",
      role: "",
    },
    validationSchema,
    onSubmit: async (values: InviteMemberFormValues) => {
      await saveMember(values);
      formik.resetForm();
    },
  });

  const { values, handleSubmit, setFieldValue, handleChange } = formik;

  return (
    <RFModal {...props} boxProps={{ className: classes.container }}>
      <Grid
        container
        component={"form"}
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleSubmit();
        }}
      >
        <Typography variant="h5">Invite Member</Typography>

        <Grid container item marginTop={theme.spacing(4)} spacing={2}>
          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>Email</InputLabel>
            <TextField
              InputProps={{
                className: classes.input,
              }}
              variant="outlined"
              fullWidth
              value={values.email}
              onChange={handleChange("email")}
              error={inputError("email", formik.touched, formik.errors).error}
              helperText={
                inputError("email", formik.touched, formik.errors).display
              }
            />
          </Grid>

          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>Role</InputLabel>
            <Select
              className={classes.input}
              variant="outlined"
              fullWidth
              value={values.role}
              onChange={(e) => setFieldValue("role", e.target.value)}
            >
              {[...roles]
                .sort((a, b) => b.level - a.level)
                .map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    {role.description}
                  </MenuItem>
                ))}
            </Select>
          </Grid>

          <Grid
            container
            item
            xs={12}
            justifyContent={"space-between"}
            marginTop={theme.spacing(4)}
          >
            <Grid item>
              <Button
                color="secondary"
                className={` ${classes.button} ${classes.backButton}`}
                onClick={props.onClose}
              >
                Cancel
              </Button>
            </Grid>

            <Grid item>
              <LoadingButton
                variant="contained"
                color="primary"
                className={classes.button}
                loading={isLoading}
                label="Save"
                type="submit"
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </RFModal>
  );
};

export default InviteMemberModal;
